import { z } from 'zod';
import { localeSchema, uuid } from './common.js';
import { sessionPublicSchema } from './user.js';
/**
 * Auth form / WS-op input DTOs (F12/F42/F43/F44). Each is the ONE schema used by
 * both the React Hook Form (`zodResolver`) and the server op handler.
 *
 * Every `.min/.max/.email/.regex` carries a catalog-key `message` (E6/E18) —
 * resolved to the user's locale by `toUserAlert` at the edge.
 */
const email = z.string().email('errors:auth.email.invalid').max(320, 'errors:auth.email.tooLong');
/** Password policy: argon2id on the server; enforce length bounds at the boundary. */
const password = z
    .string()
    .min(8, 'errors:auth.password.tooShort')
    .max(200, 'errors:auth.password.tooLong');
export const loginInput = z.object({
    email,
    password: z.string().min(1, 'errors:auth.password.required'),
});
export const signupInput = z.object({
    email,
    password,
    displayName: z.string().trim().min(1, 'errors:auth.displayName.required').max(120).optional(),
    locale: localeSchema.optional(),
    /** Referral attribution (F72) — the referring PUBLIC share's id, carried
     * by the `?ref=` query param on the share/discover URL the signup started
     * from (`referralAttributionParam`, `./share.js`). Optional: most signups
     * carry no referral. */
    ref: uuid.optional(),
});
export const magicLinkRequestInput = z.object({
    email,
    /** Sign-up vs sign-in intent disambiguation (FEATURES §F12). */
    intent: z.enum(['sign_in', 'sign_up']).default('sign_in'),
});
/** Consuming a magic-link / email-verify token (raw token from the URL). */
export const magicLinkConsumeInput = z.object({
    token: z.string().min(1, 'errors:auth.token.required'),
});
export const passwordResetRequestInput = z.object({
    email,
});
export const passwordResetConfirmInput = z
    .object({
    token: z.string().min(1, 'errors:auth.token.required'),
    newPassword: password,
    confirmPassword: z.string().min(1, 'errors:auth.password.required'),
})
    .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'errors:auth.password.mismatch',
    path: ['confirmPassword'],
});
/** Login/signup success reply: short-lived access token (refresh is a cookie). */
export const authResult = z.object({
    accessToken: z.string(),
    userId: z.string().uuid(),
});
/** `session.revoke` (F13/F54). */
export const sessionRevokeInput = z.object({
    sessionId: z.string().uuid(),
});
/**
 * `session.list` result (F13) — named result schemas for the two ops
 * `apps/server/src/ws/dispatch.ts` already wires with an inline
 * `z.object({...})` (M0). Extending, not duplicating: the dispatch table's
 * `responseSchema` for `session.list`/`session.revoke` is a migration
 * candidate to import these instead of re-declaring the shape inline.
 */
export const sessionListResult = z.object({ sessions: z.array(sessionPublicSchema) });
export const sessionRevokeResult = z.object({ revoked: z.boolean() });
